import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Answer, Config, Content } from '../engine/types.ts';
import { FunnelContext, type FunnelCtx } from '../engine/context.ts';
import { StepRegistry, mechanic } from '../steps/index.ts';
import { Column, Footer, Header, Progress, type CtaState } from '../shell/Shell.tsx';
import { ScreenTransition } from '../shell/ScreenTransition.tsx';
import { ConsentBanner } from '../shell/ConsentBanner.tsx';
import { Toast } from '../ui/bits.tsx';
import { answersRoot, clearAnswers, loadAnswers, saveAnswers, type AnswerMap } from '../engine/answers.ts';
import { indexOf, progress, readHash, sectionLabel, stepAt, writeHash } from '../engine/navigation.ts';
import { renderDeep, stripPlaceholderDeep } from '../engine/template.ts';
import { computeProjection } from '../engine/rules/projection.ts';
import { computeArchetype } from '../engine/rules/archetype.ts';
import { allPlanValues, type PlanValues } from '../engine/plans.ts';
import type { ProjectionRule, ArchetypeRule } from '../engine/types.ts';
import { track } from '../engine/track.ts';
import { preload } from '../engine/assets.ts';
import { setHaptics } from '../engine/haptics.ts';
import { leadId, setLeadId, leadEmail, setLeadEmail, captureAttribution, sessionId, firstTouch } from '../engine/session.ts';
import { geolocate } from '../engine/geo.ts';

const publicSettings = (s: Config['settings']) => ({
  brand: s.brand,
  defaultLang: s.defaultLang,
  languages: s.languages,
  timerMinutes: s.timerMinutes,
  assetsVersion: s.assetsVersion,
  app: s.app,
  features: s.features,
});

export function Funnel({ config }: { config: Config }) {
  const { funnel, content, settings } = config;

  // A refresh mid-funnel resumes: the step is in the hash and the answers come
  // back with it. Entering from the top — no hash, or ?reset=1 — is a new run,
  // so last visit's answers must not be sitting there pre-ticked.
  const resuming = (() => {
    const fromHash = readHash();
    const fresh = !fromHash || new URLSearchParams(location.search).has('reset');
    if (fresh) clearAnswers(funnel.code);
    return fresh ? -1 : indexOf(funnel, fromHash!);
  })();

  const [index, setIndex] = useState(() => (resuming >= 0 ? resuming : 0));
  const [answers, setAnswers] = useState<AnswerMap>(() => (resuming >= 0 ? loadAnswers(funnel.code) : {}));
  const [cta, setCta] = useState<CtaState>({ label: null });
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string>(() => {
    const planStep = funnel.steps.find((s) => s.type === 'plan-page');
    return planStep?.preselected ?? settings.paddle.plans[0]?.id ?? '';
  });
  const [email, setEmail] = useState<string | null>(() => (resuming >= 0 ? leadEmail() : null));
  const backStack = useRef<number[]>([]);
  const stepShownAt = useRef(0);

  const step = stepAt(funnel, index);
  const plans = useMemo(() => allPlanValues(settings), [settings]);
  const selectedPlan: PlanValues = plans.find((p) => p.id === planId) ?? plans[0];

  const projection = useMemo(() => {
    const rule = funnel.rules?.['projection.default'] as ProjectionRule | undefined;
    return rule
      ? computeProjection(rule, answers)
      : { factor: 1, multipliers: {}, tiles: [], chart: [], fallback: true, inputs: {} };
  }, [funnel, answers]);

  const archetypeResult = useMemo(() => {
    const rule = funnel.rules?.['archetype.default'] as ArchetypeRule | undefined;
    return rule ? computeArchetype(rule, answers) : { id: content.shared.archetypes[0].id, scores: {}, matched: false };
  }, [funnel, answers, content]);

  const archetype = content.shared.archetypes.find((a) => a.id === archetypeResult.id) ?? content.shared.archetypes[0];

  const roots = useMemo(
    () => ({
      answers: answersRoot(answers),
      projection: { ...projection, factor: projection.factor },
      archetype,
      settings: publicSettings(settings),
      plan: selectedPlan,
      session: { email },
    }),
    [answers, projection, archetype, settings, selectedPlan, email],
  );

  const [reportedMissing] = useState(() => new Set<string>());
  const onMissing = useCallback((path: string) => {
    if (reportedMissing.has(path)) return;
    reportedMissing.add(path);
    if (import.meta.env.DEV) console.warn(`[template] unresolved {{${path}}}`);
    else track.emit('template_missing', { path });
  }, []);

  const render = useCallback(<T,>(node: T): T => stripPlaceholderDeep(renderDeep(node, roots, onMissing)), [roots, onMissing]);

  const renderPlan = useCallback(
    (text: string, plan: PlanValues): string =>
      stripPlaceholderDeep(renderDeep(text, { ...roots, plan }, onMissing)),
    [roots, onMissing],
  );

  const shared = useMemo(() => render(content.shared) as Content['shared'], [content, render]);
  const stepContent = useMemo(() => render(content.steps[step.id] ?? {}), [content, step.id, render]);

  /* ------------------------------------------------------------------ setup */
  useEffect(() => {
    setHaptics(settings.features.haptics);
    // first touch: the cookie is written now (once) with the ad params and the
    // funnel this visitor landed on
    firstTouch(location.search, { funnel_code: funnel.code, lang: config.lang });
    track.init(settings, {
      funnel_code: funnel.code,
      funnel_version: funnel.version,
      lang: config.lang,
      sid: sessionId(),
    });
    track.emit('funnel_view', { referrer: document.referrer });
    if (settings.features.consentBanner === 'auto') {
      let done = false;
      geolocate(track.workerBase()).then((geo) => {
        if (done) return;
        done = true;
        track.applyGeo(geo);
      });
    }
  }, []);

  /* ------------------------------------------------- per-step side effects */
  useEffect(() => {
    const now = Date.now();
    const since = now - stepShownAt.current;
    stepShownAt.current = now;
    writeHash(step.id);
    window.scrollTo(0, 0);
    track.setContext({
      step_id: step.id,
      step_type: step.type,
      step_index: index,
      section: funnel.sections[step.section],
      mechanic: mechanic(step.type, step.variant, step.kind),
      archetype: archetypeResult.matched ? archetypeResult.id : undefined,
    });
    track.emit('step_view', { ms_since_prev: since });

    if (settings.features.preloadNext) {
      const next = funnel.steps[index + 1];
      if (next) preload([next.asset, next.before, next.after, ...(next.type === 'hook' ? [content.shared.hook.asset] : [])]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, step.id]);

  useEffect(() => saveAnswers(funnel.code, answers), [answers, funnel.code]);

  /* -------------------------------------------------------------- actions */
  const goToIndex = useCallback(
    (next: number) => {
      setIndex((current) => {
        if (next === current) return current;
        backStack.current.push(current);
        setCta({ label: null });
        return Math.max(0, Math.min(funnel.steps.length - 1, next));
      });
    },
    [funnel.steps.length],
  );

  const ctx: FunnelCtx = {
    config,
    shared,
    answers,
    stepIndex: index,
    projection,
    archetype,
    plans,
    selectedPlan,
    leadEmail: email,
    selectPlan: setPlanId,
    setAnswer: (key, value: Answer) => {
      setAnswers((prev) => ({ ...prev, [key]: value }));
      const flat = value as Record<string, unknown>;
      const shown = Array.isArray(flat.values) ? (flat.values as string[]).join(',') : (flat.value ?? flat.frame ?? '');
      // the first answer of the session, whichever question it is on
      track.emit('quiz_start', { answer_key: key });
      track.emit('step_answer', { answer_key: key, value: shown, ms_on_step: Date.now() - stepShownAt.current });
    },
    next: () => goToIndex(index + 1),
    goTo: (id) => {
      const i = indexOf(funnel, id);
      if (i >= 0) goToIndex(i);
    },
    back: () => {
      const previous = backStack.current.pop();
      track.emit('step_back');
      setCta({ label: null });
      setIndex(previous ?? Math.max(0, index - 1));
    },
    setCta,
    toast: (message, ms = 2000) => {
      setToastMsg(message);
      setTimeout(() => setToastMsg(null), ms);
    },
    render,
    renderPlan,
    event: (name, params) => track.emit(name, params),
    sinceStepView: () => Date.now() - stepShownAt.current,
    submitLead: async (value: string) => {
      setEmail(value);
      setLeadEmail(value);
      const res = await fetch(`${track.workerBase()}/lead`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          sid: sessionId(),
          email: value,
          funnel_code: funnel.code,
          lang: config.lang,
          answers,
          archetype: archetypeResult.id,
          projection,
          attribution: captureAttribution(),
          ga_client_id: document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/)?.[1] ?? null,
        }),
      });
      if (!res.ok) throw new Error(`lead failed: ${res.status}`);
      const data = (await res.json()) as { lead_id?: string };
      if (data.lead_id) setLeadId(data.lead_id);
      const email_hash = await track.identify(value);
      track.emit('lead', { email_hash, lead_id: data.lead_id ?? leadId() });
    },
  };

  const StepComponent = StepRegistry[step.type];
  const showShell = step.type !== 'checkout-handoff';
  const isLoader = step.type === 'loader';

  return (
    <FunnelContext.Provider value={ctx}>
      <Column stepId={step.id} stepType={step.type}>
        {showShell ? (
          <>
            <Header canGoBack={index > 0 && !isLoader} onBack={ctx.back} brand={settings.brand.name} />
            <Progress label={content.sections?.[step.section ?? 0] ?? sectionLabel(funnel, index)} segments={progress(funnel, index)} />
          </>
        ) : null}

        <ScreenTransition stepKey={step.id} scrollable={step.type === 'plan-page'}>
          <StepComponent step={step} content={stepContent} ctx={ctx} />
        </ScreenTransition>

        <Toast message={toastMsg} />
        <Footer cta={cta} />
        <ConsentBanner text={{ body: content.shared.ui?.consentBody, decline: content.shared.ui?.consentDecline, accept: content.shared.ui?.consentAccept }} />
      </Column>
    </FunnelContext.Provider>
  );
}
